import type { AgentNodeData } from '@saaga/shared/interfaces/agency-node.interface'
import type { Edge, Node, NodeTypes } from '@xyflow/react'
import { Background, Controls, MiniMap, Panel, ReactFlow } from '@xyflow/react'
import { type ReactNode, useMemo } from 'react'

import {
  BACKGROUND_CONFIG,
  MINIMAP_CONFIG,
  REACT_FLOW_CONFIG,
  REACT_FLOW_FIT_VIEW_OPTIONS,
} from './agency-flow-canvas.config'
import { AgencyFlowCanvasControls } from './agency-flow-canvas-controls.atom'
import { AgentNodeAtom } from './agent-node.atom'
import type { useAgencyFlowCanvas } from './use-agency-flow-canvas.hook'

interface AgencyFlowReactFlowProps {
  canvas: ReturnType<typeof useAgencyFlowCanvas>
  onExport: () => void
  onImport: (file: File) => void
}

const miniMapNodeColor = (node: Node): string => (node.data?.isEntryPoint ? '#f59e0b' : '#6366f1')

export const AgencyFlowReactFlow = ({
  canvas,
  onExport,
  onImport,
}: AgencyFlowReactFlowProps): ReactNode => {
  const nodeTypes: NodeTypes = useMemo(
    () => ({
      agent: (props: { id: string; data: AgentNodeData; selected?: boolean }) => (
        <AgentNodeAtom {...props} onDelete={(nodeId) => canvas.deleteHandlerRef.current(nodeId)} />
      ),
    }),
    [canvas.deleteHandlerRef]
  )
  return (
    <ReactFlow
      nodes={canvas.agencyFlow.nodeList as Node[]}
      edges={canvas.agencyFlow.edgeList as Edge[]}
      onNodesChange={canvas.agencyFlow.onNodesChange}
      onEdgesChange={canvas.agencyFlow.onEdgesChange}
      onConnect={canvas.agencyFlow.onConnect}
      onConnectEnd={canvas.handlers.handleConnectEnd}
      onNodeClick={canvas.handlers.handleNodeClick}
      onEdgeClick={canvas.handlers.handleEdgeClick}
      onPaneClick={canvas.handlers.handlePaneClick}
      nodeTypes={nodeTypes}
      connectionLineType={REACT_FLOW_CONFIG.connectionLineType}
      fitView
      fitViewOptions={REACT_FLOW_FIT_VIEW_OPTIONS}
      minZoom={REACT_FLOW_CONFIG.minZoom}
      maxZoom={REACT_FLOW_CONFIG.maxZoom}
      defaultEdgeOptions={REACT_FLOW_CONFIG.defaultEdgeOptions}
    >
      <Background {...BACKGROUND_CONFIG} />
      <Controls position="bottom-left" />
      <MiniMap {...MINIMAP_CONFIG} nodeColor={miniMapNodeColor} />
      <Panel position="top-right">
        <AgencyFlowCanvasControls
          onAutoLayout={canvas.handlers.handleAutoLayout}
          onAddNode={canvas.handlers.handleAddNode}
          onExport={onExport}
          onImport={onImport}
        />
      </Panel>
    </ReactFlow>
  )
}

AgencyFlowReactFlow.displayName = 'AgencyFlowReactFlow'
